import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const FLIGHT_DURATION_MS = 350;
const FLIGHT_EASING = "cubic-bezier(0.4, 0, 1, 1)";
const MAX_GHOST_HEIGHT = 160;

export interface StrategyCardFlightProps {
  /** Bounding rect of the recommended card, captured at click (viewport coordinates). */
  sourceRect: DOMRect;
  /** The Prioritized count badge, falling back to the Prioritized tab trigger. `null` skips the flight. */
  targetElement: HTMLElement | null;
  /** The strategy's `gapTitle`, shown on the ghost. */
  gapTitle: string;
  /** Called exactly once: on natural finish, or synchronously on mount when the flight is skipped. */
  onLand: () => void;
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function canUseWaapi(): boolean {
  return typeof Element !== "undefined" && typeof Element.prototype.animate === "function";
}

/**
 * Ghost "card flight": a lightweight clone of a card that shrinks and flies toward a
 * target element, then disappears. Connects the `Prioritize` click to the destination it
 * lands in without navigating the user away from `Other strategies`.
 *
 * This component only flies and calls back once — the move between lists, the counts, the
 * badge's celebrate and the toast belong to whoever mounts it (WEB-007).
 */
export function StrategyCardFlight({
  sourceRect,
  targetElement,
  gapTitle,
  onLand,
}: StrategyCardFlightProps) {
  const ref = useRef<HTMLDivElement>(null);
  const onLandRef = useRef(onLand);
  onLandRef.current = onLand;

  const shouldFly = targetElement !== null && canUseWaapi() && !prefersReducedMotion();

  useEffect(() => {
    if (!shouldFly) {
      onLandRef.current();
      return;
    }

    const el = ref.current;
    if (!el || !targetElement) {
      onLandRef.current();
      return;
    }

    const targetRect = targetElement.getBoundingClientRect();
    const startWidth = sourceRect.width;
    const startHeight = Math.min(sourceRect.height, MAX_GHOST_HEIGHT);
    const deltaX = targetRect.left + targetRect.width / 2 - (sourceRect.left + startWidth / 2);
    const deltaY = targetRect.top + targetRect.height / 2 - (sourceRect.top + startHeight / 2);

    const animation = el.animate(
      [
        { transform: "translate3d(0,0,0) scale(1)", opacity: 1 },
        { transform: `translate3d(${deltaX}px,${deltaY}px,0) scale(0)`, opacity: 1 },
      ],
      {
        duration: FLIGHT_DURATION_MS,
        easing: FLIGHT_EASING,
        fill: "forwards",
      },
    );

    // Once cancelled — including React Strict Mode's double-invoke, which cancels the
    // first effect's animation in dev — a later 'finish' must not advance the sequence.
    // `settled` also guards against a stray repeat 'finish' calling onLand twice.
    let settled = false;
    const handleFinish = () => {
      if (settled) return;
      settled = true;
      onLandRef.current();
    };
    const handleCancel = () => {
      settled = true;
    };

    animation.addEventListener("finish", handleFinish);
    animation.addEventListener("cancel", handleCancel);

    return () => {
      settled = true;
      animation.removeEventListener("finish", handleFinish);
      animation.removeEventListener("cancel", handleCancel);
      animation.cancel();
    };
    // sourceRect/targetElement/gapTitle are captured once per flight; the caller mounts a
    // fresh instance for each prioritize.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFly]);

  if (!shouldFly) {
    return null;
  }

  return createPortal(
    <div
      ref={ref}
      data-strategy-card-flight
      aria-hidden
      className="pointer-events-none fixed z-[60] overflow-hidden rounded-md border border-primary bg-card px-4 py-3 shadow-lg"
      style={{
        top: sourceRect.top,
        left: sourceRect.left,
        width: sourceRect.width,
        height: Math.min(sourceRect.height, MAX_GHOST_HEIGHT),
        transformOrigin: "center",
      }}
    >
      {/* eslint-disable-next-line signal/no-unwrapped-clipped-text -- decorative aria-hidden flight ghost, pointer-events-none, unmounted within 350ms so a tooltip could never open */}
      <p className="truncate text-sm font-semibold text-foreground">{gapTitle}</p>
      <p className="mt-1 text-xs text-muted-foreground">Prioritized</p>
    </div>,
    document.body,
  );
}
